import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import axios from 'axios';
import { ReactComponent as Blazon } from '../images/blazon.svg';
import { CircularProgress } from '@mui/material';
import '../css/DetailsStyles.css';

dayjs.extend(customParseFormat);


function Details() {
    const location = useLocation();
    const navigate = useNavigate();
    const { msgNumber: routeMsgNumber, carNum: routeCarNum } = useParams();
    const stateData = location.state?.data;
    const searchParams = new URLSearchParams(location.search);
    const msgNumber = routeMsgNumber || searchParams.get('msgNumber') || stateData?.msg_penalty_number;
    const carNum = routeCarNum || searchParams.get('carNum') || stateData?.car_number;
    const numericMsgNumber = msgNumber ? String(msgNumber).replace(/\D/g, '') : null;
    
    // Нормалізуємо URL: якщо користувач прийшов через query параметри (наприклад, з LiqPay),
    // перенаправляємо на нормалізований URL з параметрами в шляху
    useEffect(() => {
        // Створюємо новий URLSearchParams з поточного location.search
        const currentSearchParams = new URLSearchParams(location.search);
        const queryMsgNumber = currentSearchParams.get('msgNumber');
        const queryCarNum = currentSearchParams.get('carNum');
        
        // Перевіряємо, чи є query параметри, але немає route параметрів
        if ((queryMsgNumber || queryCarNum) && !routeMsgNumber && !routeCarNum) {
            if (queryMsgNumber && queryCarNum) {
                const normalizedMsgNumber = String(queryMsgNumber).replace(/\D/g, '');
                const newUrl = `/details/${normalizedMsgNumber}/${queryCarNum}`;
                navigate(newUrl, { replace: true });
                return;
            }
        }
    }, [location.search, routeMsgNumber, routeCarNum, navigate]);
    
    // Нормалізуємо URL, якщо в ньому є нецифрові символи (наприклад, "UZ2530473")
    // Але зберігаємо query параметри (наприклад, fromPayment)
    useEffect(() => {
        if (routeMsgNumber && routeCarNum) {
            const normalizedMsgNumber = String(routeMsgNumber).replace(/\D/g, '');
            // Якщо номер постанови містить нецифрові символи, перенаправляємо на нормалізований URL
            if (normalizedMsgNumber !== routeMsgNumber && normalizedMsgNumber) {
                const currentSearchParams = new URLSearchParams(location.search);
                const queryString = currentSearchParams.toString();
                const newUrl = `/details/${normalizedMsgNumber}/${routeCarNum}${queryString ? `?${queryString}` : ''}`;
                navigate(newUrl, { replace: true });
            }
        }
    }, [routeMsgNumber, routeCarNum, location.search, navigate]);

    const [data, setData] = useState(null);
    const [basePenaltyAmount, setBasePenaltyAmount] = useState(0);
    const [penaltyAmount, setPenaltyAmount] = useState(0);
    const [serviceFee, setServiceFee] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [status, setStatus] = useState("Очікує оплату");
    const [isPaid, setIsPaid] = useState(false);
    const [discountActive, setDiscountActive] = useState(false);
    const [daysLeftForDiscount, setDaysLeftForDiscount] = useState(0);
    const [isGracePeriodActive, setIsGracePeriodActive] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [isCheckingPayment, setIsCheckingPayment] = useState(false);
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const violationDate = data?.violation_date ? dayjs(data.violation_date) : null;
    const [errors, setErrors] = useState({
        email: '',
        fullName: '',
        penaltyAmount: ''
    });
    const [isEditingAmount, setIsEditingAmount] = useState(false);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

    useEffect(() => {
        const normalize = (raw) => {
            if (!raw) return null;
            return {
                msg_penalty_number: raw.msg_penalty_number ?? raw.msgPenaltyNumber ?? msgNumber ?? null,
                car_number: raw.car_number ?? raw.carNum ?? carNum ?? null,
                car_brand: raw.car_brand ?? raw.carBrand ?? null,
                car_model: raw.car_model ?? raw.carModel ?? null,
                violation_date: raw.violation_date ?? raw.violationDate ?? null,
                violation_time: (() => {
                    const time = raw.violation_time ?? raw.violationTime ?? raw.time ?? null;
                    if (time && typeof time === 'string') {
                        // Обрізаємо мікросекунди, якщо вони є
                        return time.split('.')[0];
                    }
                    return time;
                })(),
                address: raw.address ?? null,
                article: raw.article ?? null,
                article_part: raw.article_part ?? raw.articlePart ?? null,
                clause: raw.clause ?? null,
                penalty_amount: raw.penalty_amount ?? (raw.penaltyAmount != null ? parseFloat(raw.penaltyAmount) : null),
                fabules: raw.fabules ?? null,
                photos: raw.photos ?? [],
                payment_amount: raw.payment_amount ?? raw.decreeData?.paymentAmount ?? null,
                payment_date: raw.payment_date ?? raw.decreeData?.paymentDate ?? null,
                gracePeriod: raw.gracePeriod ?? raw.grace_period ?? false,
                isPaid: raw.isPaid ?? false,
                is_paid: raw.is_paid ?? false,
            };
        };

        const initFromState = () => {
            if (stateData) {
                const normalized = normalize(stateData);
                setData(normalized);
                const amount = parseFloat(normalized?.penalty_amount || 0);
                const numericAmount = isNaN(amount) ? 0 : amount;
                setBasePenaltyAmount(numericAmount);
                const paid = (() => {
                    // Штраф ПОВНІСТЮ СПЛАЧЕНИЙ тільки якщо є дата зарахування ТА сума зарахування
                    if (normalized?.payment_date && normalized?.payment_amount && parseFloat(normalized.payment_amount) > 0) {
                        const paidAmount = parseFloat(normalized.payment_amount);
                        const penaltyAmount = parseFloat(normalized.penalty_amount) || 0;
                        
                        // Повна оплата
                        if (paidAmount >= penaltyAmount) return true;
                        
                        // Пільгова оплата (50%)
                        if (normalized?.gracePeriod && paidAmount >= (penaltyAmount * 0.5)) return true;
                    }
                    
                    return false;
                })();
                setIsPaid(!!paid);
                // Статус встановлюється в getStatusInfo() на основі даних
                return true;
            }
            return false;
        };

        const fetchFromApi = async () => {
            if (numericMsgNumber && carNum) {
                try {
                    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
                    //const baseUrl = 'http://127.0.0.1:3000';
                    const response = await axios.post(`${baseUrl}/api/getFine`, {
                        msgNumber: numericMsgNumber,
                        platoNumber: carNum
                    });
                    if (response.data) {
                        const normalized = normalize(response.data);
                        setData(normalized);
                        const amount = parseFloat(normalized?.penalty_amount || 0);
                        const numericAmount = isNaN(amount) ? 0 : amount;
                        setBasePenaltyAmount(numericAmount);
                        const paid = (() => {
                            // Логіка визначення оплати
                            if (normalized?.payment_date) return true;
                            if (!normalized?.payment_amount || parseFloat(normalized.payment_amount) <= 0) return false;
                            
                            const paidAmount = parseFloat(normalized.payment_amount);
                            const penaltyAmount = parseFloat(normalized.penalty_amount) || 0;
                            
                            // Якщо сплачена повна сума
                            if (paidAmount >= penaltyAmount) return true;
                            
                            // Якщо пільговий період і сплачено 50% або більше
                            if (normalized?.gracePeriod && paidAmount >= (penaltyAmount * 0.5)) return true;
                            
                            return false;
                        })();
                        setIsPaid(!!paid);
                        // Статус встановлюється в getStatusInfo() на основі даних
                    }
                } catch (error) {
                    console.error('Error fetching fine details:', error);
                }
            }
        };

        const initialized = initFromState();
        if (!initialized) {
            fetchFromApi();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [msgNumber, carNum]);

    useEffect(() => {
        if (!data || basePenaltyAmount === 0) return; // Чекаємо, поки дані завантажаться
        
        const amount = Number.isFinite(basePenaltyAmount) ? basePenaltyAmount : 0;
        
        // Визначаємо чи є пільговий період:
        // 1. З бекенду (data.gracePeriod або data.grace_period)
        // 2. Обчислення на фронтенді (якщо пройшло <= 10 днів від дати порушення)
        const isGracePeriodActive = (() => {
            // Перевірка з бекенду
            if (data.gracePeriod === true || data.grace_period === true) {
                return true;
            }
            // Обчислення на фронтенді
            if (!isPaid && violationDate) {
                const daysPassed = dayjs().diff(violationDate, 'day');
                if (daysPassed <= 10 && daysPassed >= 0) {
                    return true;
                }
            }
            return false;
        })();
        
        // Показуємо суму штрафу в полі редагування тільки якщо користувач не редагує
        if (!isEditingAmount) {
            // Якщо активний пільговий період, встановлюємо половину вартості за замовчуванням
            if (isGracePeriodActive && !isPaid && amount > 0) {
                const discountAmount = Math.ceil(amount * 0.5);
                setPenaltyAmount(discountAmount);
            } else {
                setPenaltyAmount(amount);
            }
        }
        
        // Обчислюємо комісію від поточної суми
        const currentAmount = isEditingAmount 
            ? penaltyAmount 
            : (isGracePeriodActive && !isPaid && amount > 0 ? Math.ceil(amount * 0.5) : amount);
        const fee = Math.ceil(currentAmount * 0.15);
        setServiceFee(fee);
        setTotalAmount(currentAmount + fee);
        
        // Зберігаємо стан пільгового періоду
        setIsGracePeriodActive(isGracePeriodActive);
        
        // Визначаємо чи є пільговий період для інформаційних цілей
        if (!isPaid && violationDate) {
            const daysPassed = dayjs().diff(violationDate, 'day');
            if (daysPassed <= 10 && daysPassed >= 0) {
                setDiscountActive(true);
                setDaysLeftForDiscount(10 - daysPassed);
            } else {
                setDiscountActive(false);
            }
        } else {
            setDiscountActive(false);
        }
    }, [violationDate, basePenaltyAmount, isPaid, isEditingAmount, penaltyAmount, data?.gracePeriod, data?.grace_period]);

    // removed previous recalculation tied to penaltyAmount to avoid race conditions

    useEffect(() => {
        const checkPaymentStatus = async () => {
            if (numericMsgNumber && isCheckingPayment) {
                try {
                    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
                    const orderId = `order_${numericMsgNumber}`;
                    const response = await axios.post(`${baseUrl}/api/checkPaymentStatus`, {
                        orderId: orderId
                    });
                    setPaymentStatus(response.data);
                    if (response.data.status === 'paid') {
                        setIsCheckingPayment(false);
                        // Перезавантажуємо дані для оновлення статусу
                        window.location.reload();
                    } else if (response.data.status === 'pending') {
                        setTimeout(checkPaymentStatus, 5000);
                    }
                } catch (error) {
                    console.error("Error checking payment status:", error);
                    setIsCheckingPayment(false);
                }
            }
        };

        checkPaymentStatus();
    }, [numericMsgNumber, isCheckingPayment, isPaid]);

    // If redirected back from LiqPay, start checking payment status
    // We use 'fromPayment' query parameter to know user just returned from payment
    useEffect(() => {
        const currentSearchParams = new URLSearchParams(location.search);
        const fromPayment = currentSearchParams.get('fromPayment');
        // Only process if we have route parameters (not query parameters)
        // This ensures we don't conflict with URL normalization logic
        if (fromPayment === 'true' && routeMsgNumber && routeCarNum && numericMsgNumber && carNum) {
            setIsCheckingPayment(true);
            // Remove the query parameter from URL after starting check
            // Use setTimeout to ensure state is set before navigation
            setTimeout(() => {
                navigate(`/details/${numericMsgNumber}/${carNum}`, { replace: true });
            }, 0);
        }
    }, [location.search, routeMsgNumber, routeCarNum, numericMsgNumber, carNum, navigate]);

    const handlePenaltyChange = (e) => {
        try {
            const value = e.target.value.replace(/[^\d]/g, '');
            const numericValue = parseInt(value) || 0;
            setPenaltyAmount(numericValue);
            setIsEditingAmount(true);
            
            // Calculate service fee (15%)
            const fee = Math.ceil(numericValue * 0.15);
            setServiceFee(fee);
            
            // Calculate total
            setTotalAmount(numericValue + fee);
        } catch (error) {
            console.warn('Error in handlePenaltyChange:', error);
        }
    };

    const handleEmailChange = (e) => {
        try {
            setEmail(e.target.value);
        } catch (error) {
            console.warn('Error in handleEmailChange:', error);
        }
    };

    const handleFullNameChange = (e) => {
        try {
            setFullName(e.target.value);
        } catch (error) {
            console.warn('Error in handleFullNameChange:', error);
        }
    };

    // Auto-fill handlers
    const handleEmailAutoFill = (e) => {
        try {
            setEmail(e.target.value);
        } catch (error) {
            console.warn('Auto-fill error for email:', error);
        }
    };

    const handleFullNameAutoFill = (e) => {
        try {
            setFullName(e.target.value);
        } catch (error) {
            console.warn('Auto-fill error for fullName:', error);
        }
    };

    const handlePenaltyAutoFill = (e) => {
        try {
            const value = e.target.value.replace(/[^\d]/g, '');
            const numericValue = parseInt(value) || 0;
            setPenaltyAmount(numericValue);
            setIsEditingAmount(true);
            
            // Calculate service fee (15%)
            const fee = Math.ceil(numericValue * 0.15);
            setServiceFee(fee);
            
            // Calculate total
            setTotalAmount(numericValue + fee);
        } catch (error) {
            console.warn('Auto-fill error for penaltyAmount:', error);
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('uk-UA').format(amount);
    };

    const openPhotoModal = (index) => {
        setSelectedPhotoIndex(index);
        setIsPhotoModalOpen(true);
    };

    const closePhotoModal = () => {
        setIsPhotoModalOpen(false);
        setSelectedPhotoIndex(null);
    };

    const nextPhoto = () => {
        if (data.photos && data.photos.length > 0) {
            setSelectedPhotoIndex((prev) => 
                prev === data.photos.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevPhoto = () => {
        if (data.photos && data.photos.length > 0) {
            setSelectedPhotoIndex((prev) => 
                prev === 0 ? data.photos.length - 1 : prev - 1
            );
        }
    };

    const validateForm = () => {
        const newErrors = {
            email: '',
            fullName: '',
            penaltyAmount: ''
        };

        if (!email) {
            newErrors.email = 'Email є обов\'язковим полем';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Введіть коректний email';
        }

        if (!fullName) {
            newErrors.fullName = 'ПІБ є обов\'язковим полем';
        }

        if (!penaltyAmount || penaltyAmount <= 0) {
            newErrors.penaltyAmount = 'Введіть суму штрафу';
        }

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handlePayment = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://payfine.com.ua';
            const paymentData = {
                amount: totalAmount,
                currency: 'UAH',
                description: `Оплата штрафу для автомобіля ${carNum}`,
                orderId: `order_${msgNumber}`,
                carNumber: carNum,
                fullName: fullName,
                penaltyAmount: penaltyAmount
            };
            
            const response = await axios.post(`${baseUrl}/api/createLiqPayPayment`, paymentData);

            const { data, signature } = response.data;
            const form = document.createElement('form');
            form.setAttribute('method', 'POST');
            form.setAttribute('action', 'https://www.liqpay.ua/api/3/checkout');
            form.setAttribute('accept-charset', 'utf-8');

            const dataInput = document.createElement('input');
            dataInput.setAttribute('type', 'hidden');
            dataInput.setAttribute('name', 'data');
            dataInput.setAttribute('value', data);
            form.appendChild(dataInput);

            const signatureInput = document.createElement('input');
            signatureInput.setAttribute('type', 'hidden');
            signatureInput.setAttribute('name', 'signature');
            signatureInput.setAttribute('value', signature);
            form.appendChild(signatureInput);

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);

            setIsCheckingPayment(true);
        } catch (error) {
            console.error("Error creating payment:", error);
        }
    };

    const getStatusInfo = () => {
        // Використовуємо isPaid з бекенду (вже перерахований)
        if (data?.isPaid) {
            return {
                class: "paid",
                text: "✅ Сплачено"
            };
        }
        
        // Якщо є is_paid в базі (кошти списані через LiqPay, але ще не зараховані в казначейство)
        if (data?.is_paid && !data?.isPaid) {
            return {
                class: "awaiting",
                text: "⏳ Очікує зарахування"
            };
        }
        
        // Якщо йде перевірка платежу
        if (isCheckingPayment) {
            return {
                class: "processing",
                text: "🔄 Обробка платежу"
            };
        }
        
        // За замовчуванням - очікує оплату
        return {
            class: "pending",
            text: "💳 Очікує оплату"
        };
    };

    if (!data) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <CircularProgress />
        </div>
    );

    return (
        <div className="details-container">
            <div className="header-gradient">
                <div className="header-content clickable" onClick={() => navigate('/') }>
                    <Blazon className="header-logo" width={80} height={80} />
                    <h4 className="header-title">Сплатити штраф за номером постанови</h4>
                </div>
            </div>

            <div className="details-content">
                <div className="main-content">
                    <div className="form-header">
                        <div className="decree-info">
                            <h2>Постанова про адміністративне правопорушення</h2>
                            <div className="decree-details">
                                <div className="decree-info-group">
                                    <div className="decree-number">№ {msgNumber}</div>
                                    <div className={`status-badge ${getStatusInfo().class}`}>
                                        {getStatusInfo().text}
                                    </div>
                                </div>
                                <div className="car-info">
                                    <span className="car-number">{carNum}</span>
                                    {data.car_brand && (
                                        <span className="car-model">{data.car_brand} {data.car_model}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="violation-info-section">
                        <div className="violation-section-header">
                            <div className="violation-icon">📋</div>
                            <div className="violation-header-content">
                                <h3>Інформація про правопорушення</h3>
                                <p className="violation-subtitle">Деталі порушення ПДР</p>
                            </div>
                        </div>
                        
                        {discountActive && (
                            <div className="discount-badge">
                                <div className="discount-icon">🎯</div>
                                <div className="discount-content">
                                    <div className="discount-title">Пільговий період</div>
                                    <div className="discount-description">
                                        Можна сплатити <strong>50% від суми</strong> ще <span className="days-remaining">{daysLeftForDiscount} днів</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="violation-details">
                            {/* Photos section for mobile */}
                            <div className="photos-section-mobile">
                                <h4>Фото порушення</h4>
                                <div className="details-photo-grid">
                                    {data.photos && data.photos.length > 0 ? (
                                        data.photos.map((photoUrl, index) => {
                                            const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
                                            const proxyUrl = `${baseUrl}/api/image-proxy?url=${encodeURIComponent(photoUrl)}`;
                                            return (
                                                <div key={index} className="photo-container" onClick={() => openPhotoModal(index)}>
                                                    <img 
                                                        src={proxyUrl} 
                                                        alt={`Фото порушення ${index + 1}`} 
                                                        className="details-photo"
                                                        onError={(e) => {
                                                            console.error('Failed to load image:', photoUrl);
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="no-photos">Фото відсутні</div>
                                    )}
                                </div>
                            </div>

                            <div className="violation-main-info">
                                <div className="info-group">
                                    <div className="info-item">
                                        <span className="label">Дата порушення:</span>
                                        <span className="value">{data.violation_date}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Час:</span>
                                        <span className="value">{data.violation_time || 'Не вказано'}</span>
                                    </div>
                                </div>
                                
                                <div className="info-group">
                                    <div className="info-item">
                                        <span className="label">Місце порушення:</span>
                                        <span className="value">{data.address || "Невідомо"}</span>
                                    </div>
                                </div>

                                <div className="info-group">
                                    <div className="info-item">
                                        <span className="label">Порушення за статтею:</span>
                                        <span className="value">Стаття {data.article} частина {data.article_part} КУпАП</span>
                                    </div>
                                </div>
                            </div>

                            <div className="violation-description">
                                <h4>Суть порушення:</h4>
                                <p>{data.fabules || "Опис порушення відсутній"}</p>
                            </div>
                        </div>
                    </div>

                    <div className="recipient-section">
                        <div className="recipient-section-header">
                            <div className="recipient-icon">🏛️</div>
                            <div className="recipient-header-content">
                                <h3>Кінцевий отримувач</h3>
                                <p className="recipient-subtitle">Реквізити для оплати</p>
                            </div>
                        </div>
                        <div className="recipient-item">
                            <span className="recipient-label">Отримувач:</span>
                            <span className="recipient-value">ГУК у Зак.обл/Ужгородська ТГ/21081100</span>
                        </div>
                        <div className="recipient-item">
                            <span className="recipient-label">ЄДРПОУ:</span>
                            <span className="recipient-value">37975895</span>
                        </div>
                        <div className="recipient-item">
                            <span className="recipient-label">IBAN:</span>
                            <span className="recipient-value">UA568999980314040542000007493</span>
                        </div>
                        <div className="recipient-item">
                            <span className="recipient-label">Призначення платежу:</span>
                            <span className="recipient-value">{carNum};UZ{msgNumber};{fullName || 'ПІБ платника'};</span>
                        </div>
                    </div>


                    <div className="payment-section">
                        <div className="payment-section-header">
                            <div className="payment-icon">💳</div>
                            <div className="payment-header-content">
                                <h3>Оплата штрафу</h3>
                                <p className="payment-subtitle">Безпечна оплата через LiqPay</p>
                            </div>
                        </div>
                        {(data?.isPaid || (data?.is_paid && data?.payment_date && data?.payment_amount && parseFloat(data.payment_amount) > 0)) ? (
                            <div className="payment-paid">
                                <div className="info-group">
                                    <div className="info-item">
                                        <span className="label">Статус:</span>
                                        <strong className="value">✅ Сплачено</strong>
                                    </div>
                                    {data.payment_amount && (
                                        <div className="info-item">
                                            <span className="label">Сума сплати:</span>
                                            <strong className="value">{formatAmount(parseFloat(data.payment_amount))} ₴</strong>
                                        </div>
                                    )}
                                    {data.payment_date && (
                                        <div className="info-item">
                                            <span className="label">Дата сплати:</span>
                                            <span className="value">{data.payment_date}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                        <div className="payment-form">
                            <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                                <label htmlFor="email">
                                    Email для отримання квитанції
                                    <span className="required">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className={`form-control ${errors.email ? 'error' : ''} ${email && !errors.email ? 'valid' : ''}`}
                                    value={email}
                                    onChange={handleEmailChange}
                                    onInput={handleEmailAutoFill}
                                    onBlur={handleEmailAutoFill}
                                    placeholder="example@email.com"
                                    autoComplete="email"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                                {errors.email && <div className="error-icon" />}
                                <div className={`error-message ${errors.email ? 'visible' : ''}`}>
                                    {errors.email}
                                </div>
                            </div>

                            <div className={`form-group ${errors.fullName ? 'has-error' : ''}`}>
                                <label htmlFor="fullName">
                                    ПІБ платника
                                    <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    className={`form-control ${errors.fullName ? 'error' : ''} ${fullName && !errors.fullName ? 'valid' : ''}`}
                                    value={fullName}
                                    onChange={handleFullNameChange}
                                    onInput={handleFullNameAutoFill}
                                    onBlur={handleFullNameAutoFill}
                                    placeholder="Іванов Іван Іванович"
                                    autoComplete="name"
                                    autoCorrect="off"
                                    autoCapitalize="words"
                                    spellCheck="false"
                                />
                                {errors.fullName && <div className="error-icon" />}
                                <div className={`error-message ${errors.fullName ? 'visible' : ''}`}>
                                    {errors.fullName}
                                </div>
                            </div>

                            <div className="payment-summary">
                                <div className="payment-row">
                                    <span>Повний штраф:</span>
                                    <span className="amount">{formatAmount(basePenaltyAmount)} ₴</span>
                                </div>
                                {isGracePeriodActive && !isPaid && (
                                    <div className="payment-row discount-applied">
                                        <span>
                                            <span className="discount-icon-small">🎯</span> Застосовано пільгу 50%
                                        </span>
                                        <span className="amount discount-amount">
                                            -{formatAmount(basePenaltyAmount - Math.ceil(basePenaltyAmount * 0.5))} ₴
                                        </span>
                                    </div>
                                )}
                                <div className="payment-row">
                                    <span>Сума штрафу:</span>
                                    <div className="amount-wrapper">
                                        <input
                                            type="text"
                                            className="amount-input editable"
                                            value={penaltyAmount}
                                            onChange={handlePenaltyChange}
                                            onInput={handlePenaltyAutoFill}
                                            onBlur={handlePenaltyAutoFill}
                                            placeholder="Введіть суму"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            title="Ви можете змінити суму самостійно"
                                        />
                                        <span className="currency"> ₴</span>
                                    </div>
                                </div>
                                <div className="payment-row">
                                    <span>Комісія сервісу</span>
                                    <span className="amount">{formatAmount(serviceFee)} ₴</span>
                                </div>
                                <div className="payment-row total-row">
                                    <strong>До сплати:</strong>
                                    <strong className="amount total-amount">{formatAmount(totalAmount)} ₴</strong>
                                </div>
                            </div>

                            <button 
                                className="details-button" 
                                onClick={handlePayment}
                                disabled={isCheckingPayment}
                            >
                                {isCheckingPayment ? 'Перевірка оплати...' : 'Сплатити'}
                            </button>
                            <div className="payment-info">
                                <div className="info-icon">ℹ️</div>
                                <div className="info-text">
                                    <strong>Важливо:</strong> Платежі зараховуються на рахунок казначейства протягом 1-5 банківських днів. Після зарахування статус штрафу автоматично оновиться.
                                </div>
                            </div>
                            <p className="details-agreement">
                                Натискаючи на кнопку "Сплатити", ви приймаєте <a href="/public_offer.pdf" target="_blank" rel="noopener noreferrer">Угоду користувача</a>
                            </p>
                        </div>
                        )}
                    </div>
                </div>

                <div className="side-content">
                    <div className="photos-section">
                        <h3>Фото порушення</h3>
                        <div className="details-photo-grid">
                            {data.photos && data.photos.length > 0 ? (
                                data.photos.map((photoUrl, index) => {
                                    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
                                    const proxyUrl = `${baseUrl}/api/image-proxy?url=${encodeURIComponent(photoUrl)}`;
                                    return (
                                        <div key={index} className="photo-container">
                                            <img 
                                                src={proxyUrl} 
                                                alt={`Фото порушення ${index + 1}`} 
                                                className="details-photo"
                                                onError={(e) => {
                                                    console.error('Failed to load image:', photoUrl);
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="details-text">Фотографії не знайдено</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Photo Modal */}
            {isPhotoModalOpen && data.photos && data.photos.length > 0 && (
                <div className="photo-modal-overlay" onClick={closePhotoModal}>
                    <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="photo-modal-close" onClick={closePhotoModal}>×</button>
                        <div className="photo-modal-image-container">
                            <img 
                                src={`${process.env.REACT_APP_API_BASE_URL || window.location.origin}/api/image-proxy?url=${encodeURIComponent(data.photos[selectedPhotoIndex])}`}
                                alt={`Фото порушення ${selectedPhotoIndex + 1}`}
                                className="photo-modal-image"
                            />
                        </div>
                        {data.photos.length > 1 && (
                            <>
                                <button className="photo-modal-prev" onClick={prevPhoto}>‹</button>
                                <button className="photo-modal-next" onClick={nextPhoto}>›</button>
                                <div className="photo-modal-counter">
                                    {selectedPhotoIndex + 1} / {data.photos.length}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Details;